import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, Sparkles, TrendingUp, Users } from "lucide-react";
import { callCreatorApplicationSubmit } from "@/lib/firebase/functions";
import { toastError } from "@/lib/toast";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/become-a-creator")({
  head: () => ({
    meta: [
      { title: "Become a Creator — Real Pranks" },
      {
        name: "description",
        content:
          "Join the Real Pranks roster. Apply to become a creator and reach our weekly subscriber audience.",
      },
      { property: "og:title", content: "Become a Creator — Real Pranks" },
      {
        property: "og:description",
        content: "Apply to become a Real Pranks creator.",
      },
    ],
  }),
  component: BecomeACreatorPage,
});

const PERKS = [
  {
    icon: Users,
    title: "Built-in audience",
    body: "Tap into a roster of subscribers who show up every Sunday for new drops.",
  },
  {
    icon: TrendingUp,
    title: "Predictable revenue",
    body: "Recurring subscriptions mean recurring payouts — not one-off ad spikes.",
  },
  {
    icon: Camera,
    title: "Production support",
    body: "Get advice on editing video, creating thumbnails and release strategy from our team in the exclusive chat group.",
  },
  {
    icon: Sparkles,
    title: "Creative freedom",
    body: "We back you on the kind of content YouTube algorithms keep punishing.",
  },
];

const DEMONETIZATION_OPTIONS = [
  { value: "100% of the time", label: "100% of the time" },
  { value: "75% of the time", label: "75% of the time" },
  { value: "50% of the time", label: "50% of the time" },
  { value: "25% of the time", label: "25% of the time" },
  { value: "0% of the time", label: "0% of the time" },
] as const;

const EMPTY_FORM = {
  demonetizationFrequency: "",
  demonetizationObstacle: "",
  youtubeLink: "",
  instagramLink: "",
  email: "",
  phone: "",
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

type RequiredField =
  | "demonetizationFrequency"
  | "demonetizationObstacle"
  | "email"
  | "phone";

const fieldErrorClass = (hasError: boolean, className?: string) =>
  cn(
    className,
    hasError &&
      "border-destructive ring-1 ring-destructive focus-visible:ring-destructive",
  );

function BecomeACreatorPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const updateField = (field: keyof typeof EMPTY_FORM, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const fieldHasError = (field: RequiredField) => {
    if (!showFieldErrors) return false;

    if (field === "email") {
      if (!form.email.trim()) return true;
      return !isValidEmail(form.email);
    }

    return form[field].trim().length === 0;
  };

  const hasRequiredFields = () =>
    form.demonetizationFrequency.trim().length > 0 &&
    form.demonetizationObstacle.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.phone.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(false);
    setErrorMessage("");
    setShowFieldErrors(true);

    if (!hasRequiredFields()) {
      const message =
        "Please complete all required fields before submitting the form.";
      setErrorMessage(message);
      toastError(message, { flow: "become-a-creator" });
      return;
    }

    if (!isValidEmail(form.email)) {
      const message = "Please enter a valid email address.";
      setErrorMessage(message);
      toastError(message, { flow: "become-a-creator" });
      return;
    }

    setSubmitting(true);
    const result = await callCreatorApplicationSubmit({
      demonetizationFrequency: form.demonetizationFrequency,
      demonetizationObstacle: form.demonetizationObstacle,
      youtubeLink: form.youtubeLink,
      instagramLink: form.instagramLink,
      email: form.email,
      phone: form.phone,
    });
    setSubmitting(false);

    if ("error" in result) {
      setErrorMessage(result.error);
      toastError(result.error, { flow: "become-a-creator" });
      return;
    }

    setForm(EMPTY_FORM);
    setShowFieldErrors(false);
    setSubmitted(true);
    toast.success(
      "Thank you, we will be in contact if we believe we can help you!",
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        {/* Hero */}
        <section className="mx-auto max-w-5xl px-4 pt-12 pb-10 md:pt-20">
          <span className="text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase">
            Creator program
          </span>
          <h1 className="mt-2 text-4xl font-bold md:text-6xl">
            Make pranks. <br className="hidden sm:block" />
            Get paid weekly.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Real Pranks is a home for prank creators who want a real audience
            and real pay without playing the algorithm game. If you've got an
            idea or an existing channel, we want to hear from you.
          </p>
        </section>

        {/* Perks */}
        <section className="mx-auto max-w-5xl px-4 pb-12">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PERKS.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[oklch(0.62_0.19_250)]/15 text-[oklch(0.62_0.19_250)]">
                  <p.icon className="h-4 w-4" />
                </span>
                <h3 className="mt-4 text-base font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Application form */}
        <section className="mx-auto max-w-5xl px-4 pb-20">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="text-2xl font-bold">Apply to be a creator</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Tell us about your channel and how demonetization affects your
              growth. We review every application.
            </p>

            <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="sm:col-span-2 lg:col-span-3">
                  <Label htmlFor="demonetizationFrequency">
                    How often do you get demonetized or age restricted on
                    YouTube?
                  </Label>
                  <Select
                    value={form.demonetizationFrequency || undefined}
                    onValueChange={(value) =>
                      updateField("demonetizationFrequency", value)
                    }
                  >
                    <SelectTrigger
                      id="demonetizationFrequency"
                      className={fieldErrorClass(
                        fieldHasError("demonetizationFrequency"),
                        "mt-1",
                      )}
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEMONETIZATION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <Label htmlFor="demonetizationObstacle">
                    Does the unpredictability of getting demonetized get in the
                    way of you growing as a creator?
                  </Label>
                  <Input
                    id="demonetizationObstacle"
                    value={form.demonetizationObstacle}
                    onChange={(e) =>
                      updateField("demonetizationObstacle", e.target.value)
                    }
                    className={fieldErrorClass(
                      fieldHasError("demonetizationObstacle"),
                      "mt-1",
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="youtubeLink">
                    What is your Youtube Channel Link?
                  </Label>
                  <Input
                    id="youtubeLink"
                    type="text"
                    autoComplete="url"
                    value={form.youtubeLink}
                    onChange={(e) => updateField("youtubeLink", e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="instagramLink">
                    What is your Instagram Page Link?
                  </Label>
                  <Input
                    id="instagramLink"
                    type="text"
                    autoComplete="url"
                    value={form.instagramLink}
                    onChange={(e) =>
                      updateField("instagramLink", e.target.value)
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email">What is your Email?</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className={fieldErrorClass(fieldHasError("email"), "mt-1")}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">What is your Phone Number?</Label>
                  <Input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    className={fieldErrorClass(fieldHasError("phone"), "mt-1")}
                  />
                </div>
              </div>

              <div className="flex flex-col items-center gap-3">
                <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? "Submitting..." : "Submit application"}
                </Button>
                {submitted ? (
                  <p className="max-w-md text-center text-sm text-green-600 dark:text-green-400">
                    Thank you, we will be in contact if we believe we can help
                    you!
                  </p>
                ) : errorMessage ? (
                  <p className="max-w-md text-center text-sm text-destructive">
                    {errorMessage}
                  </p>
                ) : null}
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
}
